import { memo, useMemo } from 'react';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useInnovationData } from './useInnovationData';

// Consistent colors by target type across all groups
const TARGET_COLORS = {
  Dev: '#3b82f6',
  Use: '#22c55e',
  Package: '#ec4899',
  'Knowledge product': '#9ca3af',
  'Capacity sharing for development': '#9ca3af',
  'Other output': '#9ca3af',
  'Other outcome': '#9ca3af',
  'Policy change': '#9ca3af',
  'Complementary innovation': '#9ca3af',
};

const SOURCE_ORDER = ['Dev', 'Use', 'Package'];
const SOURCE_LABELS = {
  Dev: 'Innovation Development',
  Use: 'Innovation Use',
  Package: 'Innovation Package',
};

export const InnovationLinkageChart = memo(function InnovationLinkageChart() {
  const { linkageData, totalCount, innovationResults } = useInnovationData();
  const yAxisWidth = useResponsiveYAxisWidth(130);
  const groups = useMemo(() => {
    if (!linkageData) return [];
    const grouped = {};
    Object.entries(linkageData.chainTypes).forEach(([label, count]) => {
      if (count === 0) return;
      const [source, target] = label.split('→');
      if (!grouped[source]) grouped[source] = { source, total: 0, targets: [] };
      grouped[source].total += count;
      grouped[source].targets.push({ target, count, fullLabel: label });
    });
    Object.values(grouped).forEach(g => g.targets.sort((a, b) => b.count - a.count));
    return SOURCE_ORDER
      .filter(s => grouped[s])
      .map(s => grouped[s])
      .concat(
        Object.values(grouped).filter(g => !SOURCE_ORDER.includes(g.source))
      );
  }, [linkageData]);
  if (!linkageData || linkageData.totalLinks === 0) return (
    <ChartWrapper title="Innovation Linkages & Bundles">
      <NoChartData message="No inter-result linkages found" detail='This chart maps connections between innovation results using the "Linked Results" field (e.g. Dev→Use, Dev→Package chains). None of the innovation results in the current selection have linkages to other results.' />
    </ChartWrapper>
  );

  const { resultsWithLinks, totalLinks, chainTypes, crossProgramLinks, sourceResultCounts } = linkageData;

  const exportData = [
    { Metric: 'Results with linkages', Value: resultsWithLinks },
    { Metric: 'Total linkages', Value: totalLinks },
    { Metric: 'Cross-program linkages', Value: crossProgramLinks },
    ...Object.entries(chainTypes).map(([type, count]) => ({ Metric: `Chain: ${type}`, Value: count })),
  ];

  return (
    <ChartWrapper title="Innovation Linkages & Bundles" info="Inter-result linkages between innovation development, use, and package results, showing how innovations connect across the pipeline." chartData={exportData} exportFilename="innovation-linkages" sourceResults={innovationResults}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-purple-700">{resultsWithLinks}</p>
          <p className="text-sm text-purple-600">Results with linkages</p>
          <p className="text-xs text-gray-500">of {totalCount} innovation results</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-blue-700">{totalLinks}</p>
          <p className="text-sm text-blue-600">Total linkages</p>
          <p className="text-xs text-gray-500">across linked results</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-green-700">{crossProgramLinks}</p>
          <p className="text-sm text-green-600">Cross-program links</p>
          <p className="text-xs text-gray-500">between different programs</p>
        </div>
      </div>

      <h5 className="text-sm font-medium text-gray-700 mb-1">Linkage Chain Types</h5>
      <p className="text-xs text-gray-400 mb-3">Counts represent individual link pairs between results, not unique results</p>

      {groups.map(group => (
        <div key={group.source} className="mb-4">
          <div className="flex items-baseline gap-1.5 mb-1">
            <h6 className="text-sm font-semibold text-gray-800">
              {SOURCE_LABELS[group.source] || group.source}
            </h6>
            <span className="text-xs text-gray-400">
              — {sourceResultCounts[group.source] || 0} result{(sourceResultCounts[group.source] || 0) !== 1 ? 's' : ''} · {group.total} link{group.total !== 1 ? 's' : ''}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(60, group.targets.length * 32 + 16)}>
            <BarChart
              data={group.targets}
              layout="vertical"
              margin={{ left: 10, right: 40, top: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} hide />
              <YAxis
                type="category"
                dataKey="target"
                tick={{ fontSize: 11 }}
                width={yAxisWidth}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.fullLabel || d.target}</div>
                    <div style={{ color: '#4b5563' }}>{d.count.toLocaleString()} result{d.count !== 1 ? 's' : ''}</div>
                  </div>
                );
              }} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} animationBegin={0} animationDuration={400}>
                {group.targets.map((entry) => (
                  <Cell key={entry.fullLabel} fill={TARGET_COLORS[entry.target] || '#9ca3af'} />
                ))}
                <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ))}
    </ChartWrapper>
  );
});
